import * as React from "react";
import { Button, Input, notification } from "antd";
import { SyntaxHighlight } from "../../components/syntaxHighlight/SyntaxHighlight";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { CodeEditor } from "../group/identityMatcher/codeEditor";
import { homeLink, request } from "../../services/cpln";

export interface AzureInstructions {
  data: {
    createSp: string;
  };
  message: string;
}

interface Props {
  instructions: AzureInstructions;
  defaultSecretName?: string;
  onSecretCreated?: (secretName: string) => void;
}
export const ProviderAzureInstructions: React.FC<Props> = ({ instructions, defaultSecretName = "", onSecretCreated }) => {
  const [subscription, setSubscription] = React.useState("");
  const [sdkJson, setSdkJson] = React.useState("");
  const [secretName, setSecretName] = React.useState(defaultSecretName);
  const [isCreating, setIsCreating] = React.useState(false);

  React.useEffect(() => {
    if (defaultSecretName && !secretName) {
      setSecretName(defaultSecretName);
    }
  }, [defaultSecretName]);

  const subscriptionCode = "<SUBSCRIPTION>";
  const createSpStep = instructions.data.createSp.replace(subscriptionCode, subscription || subscriptionCode);

  async function handleCreateSecret() {
    if (!sdkJson.trim()) {
      notification.warning({ message: "Please paste the SDK JSON content" });
      return;
    }

    const finalSecretName = secretName.trim() || defaultSecretName;
    if (!finalSecretName) {
      notification.warning({ message: "Please enter a secret name" });
      return;
    }

    try {
      setIsCreating(true);
      await request({
        method: "post",
        url: homeLink("secret"),
        body: {
          name: finalSecretName,
          kind: "secret",
          description: "Azure SDK token created inline for mk8s cluster",
          type: "opaque",
          data: {
            encoding: "plain",
            payload: sdkJson,
          },
        },
      });
      notification.success({ message: "Secret created successfully" });
      onSecretCreated?.(finalSecretName);
    } catch (e) {
      const errorMessage = e?.response?.data?.message || e.message;
      notification.error({ message: "Failed to create secret", description: errorMessage });
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <ul className="mt-1 mb-4 px-4 py-2 ml-4">
      <li className="list-decimal my-2">
        Install the{" "}
        <ExternalLink url="https://learn.microsoft.com/en-us/cli/azure/install-azure-cli" label="Azure CLI" disableCopy />{" "}
        and{" "}
        <ExternalLink
          url="https://learn.microsoft.com/en-us/cli/azure/authenticate-azure-cli"
          label="configure authentication"
          disableCopy
        />
        .
        <div className="mt-2">
          Alternatively, you can use{" "}
          <ExternalLink url="https://shell.azure.com/bash" label="Azure Cloud Shell" disableCopy /> which has the Azure
          CLI pre-installed and pre-authenticated.
        </div>
      </li>
      <li className="my-2 list-decimal">
        Open Azure Portal and go to subscriptions page, copy and paste the{" "}
        <span className="font-semibold">Subscription ID</span> below.
      </li>
      <Input
        style={{ width: 450 }}
        placeholder="Azure Subscription ID"
        value={subscription}
        onChange={(e) => setSubscription(e.target.value)}
      />
      {subscription && (
        <>
          <li className="my-2 list-decimal">Execute the following command.</li>
          <SyntaxHighlight code={createSpStep} language="bash" className="my-2" />
          <li className="my-2 list-decimal">
            Paste the contents of the generated <span className="font-semibold">sdk.json</span> file below.
          </li>
          <div className="my-2">
            <CodeEditor
              value={sdkJson}
              setValue={setSdkJson}
              language="json"
              options={{
                autoFocus: false,
                basicSetup: {
                  lineNumbers: true,
                },
                style: {
                  maxHeight: 300,
                  overflow: "auto",
                  fontSize: 14,
                },
              }}
              minHeight="150px"
            />
          </div>
          {sdkJson && (
            <>
              <li className="my-2 list-decimal">
                Optionally, enter a custom name for the secret.
              </li>
              <Input
                style={{ width: 450 }}
                placeholder={defaultSecretName ? `Default: ${defaultSecretName}` : "Secret Name"}
                value={secretName}
                onChange={(e) => setSecretName(e.target.value)}
              />
              <div className="my-4">
                <Button type="primary" onClick={handleCreateSecret} loading={isCreating}>
                  Create Secret{defaultSecretName && !secretName.trim() ? ` "${defaultSecretName}"` : ""}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </ul>
  );
};
